import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '@arabquiz/ui'
import { CircleCheck } from 'lucide-react'
import type { EditorQuestion } from './types'

type McqOptionsEditorProps = {
  question: Extract<EditorQuestion, { type: 'MultipleChoice' }>
  onChange: (next: EditorQuestion) => void
}

/**
 * Multiple-choice answer editor: a radio group over the option inputs where
 * the selected radio marks the correct option. Extracted verbatim from
 * `QuizEditor` (E1) so the quiz editor and the question-bank item form share
 * a single source of truth.
 */
export function McqOptionsEditor({ question, onChange }: McqOptionsEditorProps) {
  const { t } = useTranslation('quiz')
  const groupName = useId()

  function setOption(i: number, value: string) {
    onChange({
      ...question,
      options: question.options.map((opt, j) => (i === j ? value : opt)),
    })
  }

  function setCorrect(i: number) {
    onChange({ ...question, correctOptionIndex: i })
  }

  return (
    <fieldset>
      <legend className="block text-sm font-semibold text-brand-900 mb-2">
        {t('correctAnswer')}
      </legend>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {question.options.map((option, i) => {
          const isCorrect = question.correctOptionIndex === i
          return (
            <label
              key={`${groupName}-${i}`}
              className={
                'group flex items-center gap-3 rounded-xl border ps-3 pe-3 py-2.5 cursor-pointer transition-colors ' +
                (isCorrect
                  ? 'border-emerald-300 bg-emerald-50/70 ring-1 ring-emerald-200'
                  : 'border-brand-200 bg-white hover:border-brand-300 hover:bg-brand-50/40')
              }
            >
              <input
                type="radio"
                name={groupName}
                checked={isCorrect}
                onChange={() => setCorrect(i)}
                className="sr-only"
              />
              <span
                aria-hidden="true"
                className={
                  'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-2 transition-colors ' +
                  (isCorrect
                    ? 'border-emerald-500 bg-emerald-500 text-white'
                    : 'border-brand-300 bg-white text-transparent')
                }
              >
                <CircleCheck className="h-3.5 w-3.5" strokeWidth={3} />
              </span>
              <Input
                value={option}
                onChange={(e) => setOption(i, e.target.value)}
                onClick={(e) => e.stopPropagation()}
                placeholder={t('option', { n: i + 1 })}
                maxLength={200}
                className="border-0 bg-transparent ps-0 pe-0 focus-visible:ring-0 focus-visible:ring-offset-0"
              />
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
